import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ContactMenu from '../../components/ContactMenu';
import Footer from '../../components/Footer';
import FormErrorMessage from '../../components/FormErrorMessage';
import FormInput from '../../components/FormInput';
import FormTextArea from '../../components/FormTextArea';
import Menu from '../../components/Menu/Menu';
import SecondaryButtonBig from '../../components/SecondaryButtonBig';
import Spinner from '../../components/Spinner';
import withRecaptcha from '../../components/withRecaptcha';
import { useAlert } from '../../context/AlertProvider';
import { getToken } from '../../helpers/auth';

import MyHead from '../../components/MyHead';
import FileInput from '../../components/FileInput';
import get from 'lodash/get';
import { serialize } from 'object-to-formdata';

interface IB2BContactsData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  store: string;
  country: string;
  city: string;
  state: string;
  message: string;
  file: File | null;
  newsletter: boolean;
  contact: boolean;
}

const B2BContacts: React.FC = () => {
  const alert = useAlert();
  const token = getToken();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    watch,
    setValue
  } = useForm<IB2BContactsData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      store: '',
      country: '',
      city: '',
      state: '',
      message: '',
      file: null,
      newsletter: false,
      contact: false
    }
  });

  useEffect(() => {
    register('message', {
      required: t('REQUIRED_FIELD') as string
    });

    register('file', {
      required: t('REQUIRED_FIELD') as string
    });
  }, []);

  const onSubmit = async (values: IB2BContactsData, event: any) => {
    setLoading(true);

    // await validateRecaptchaToken({
    //   token,
    //   recaptchaToken: await getRecaptchaToken()
    // });

    const response = await fetch('/api/sendgridB2B', {
      method: 'POST',
      body: serialize({
        email: values.email,
        firstName: values.first_name,
        lastName: values.last_name,
        city: values.city,
        country: values.country,
        state: values.state,
        currentLanguage: i18n.language,
        formName: 'b2b',
        phone: values.phone,
        store: values.store,
        message: values.message,
        file: values.file
      })
    });

    if (!response.ok) {
      alert.error(t('GENERIC_ERROR'));
      setLoading(false);
      return;
    }

    reset();
    event.target.reset();

    setLoading(false);
    return alert.show(t('SEND_FORM_SUCCESS'));
  };

  const values = watch();

  return (
    <>
      <MyHead />
      <Menu />
      <div className="py-12 bg-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <ContactMenu actual_page={'b2bcontacts'} />
          <div className="bg-white py-8 px-6 rounded-lg mb-6">
            <div className="flex items-left justify-between mb-3 flex-col md:flex-row md:items-center">
              <h3 className="text-3xl font-sans">{t('CONTACT_US_B2B')}</h3>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="first_name"
                    type="text"
                    placeholder={t('NAME')}
                    invalid={Boolean(errors.first_name)}
                    {...{
                      ...register('first_name', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="first_name" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="last_name"
                    type="text"
                    placeholder={t('SURNAME')}
                    invalid={Boolean(errors.last_name)}
                    {...{
                      ...register('last_name', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="last_name" />
                </div>
              </div>

              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="email"
                    type="email"
                    placeholder={t('MAIL_ADDRESS')}
                    invalid={Boolean(errors.email)}
                    {...register('email', {
                      required: t('REQUIRED_FIELD') as string,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                        message: t('INVALID_EMAIL')
                      }
                    })}
                  />

                  <FormErrorMessage errors={errors} name="email" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="phone"
                    type="text"
                    placeholder={t('PHONE')}
                    invalid={Boolean(errors.phone)}
                    {...{
                      ...register('phone', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="phone" />
                </div>
              </div>

              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="store"
                    type="text"
                    placeholder={t('SHOP_NAME')}
                    invalid={Boolean(errors.store)}
                    {...{
                      ...register('store', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="store" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="country"
                    type="text"
                    placeholder={t('COUNTRY')}
                    invalid={Boolean(errors.country)}
                    {...{
                      ...register('country', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="country" />
                </div>
              </div>

              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="city"
                    type="text"
                    placeholder={t('CITY')}
                    invalid={Boolean(errors.city)}
                    {...{
                      ...register('city', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="city" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="state"
                    type="text"
                    placeholder={t('DISTRICT')}
                    invalid={Boolean(errors.state)}
                    {...{
                      ...register('state', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="state" />
                </div>
              </div>

              <div>
                <FormTextArea
                  id="message"
                  placeholder={t('MESSAGE')}
                  invalid={Boolean(errors.message)}
                  value={values.message}
                  onChange={({ currentTarget }) =>
                    setValue('message', currentTarget.value, {
                      shouldValidate: true
                    })
                  }
                />

                <FormErrorMessage errors={errors} name="message" />
              </div>

              <div>
                <FileInput
                  id="file"
                  label="File da caricare"
                  onChange={({ currentTarget }) =>
                    setValue('file', currentTarget.files![0], {
                      shouldValidate: true
                    })
                  }
                />

                <FormErrorMessage errors={errors} name="file" />
              </div>

              <div
                className={`relative flex items-center mt-2 py-2 px-3 border rounded ${
                  errors.newsletter ? ' border-red-500' : 'border-transparent'
                }`}
              >
                <input
                  id="contact"
                  aria-describedby="privacy-description"
                  type="checkbox"
                  className="focus:outline-none h-4 w-4 text-primary border-gray-300 rounded"
                  {...register('contact', {
                    required: t('REQUIRED_FIELD') as string
                  })}
                />
                <div className="ml-2 text-sm">
                  <label htmlFor="contact">
                    <div
                      id="newsletter-description"
                      className="text-gray-500 cursor-pointer"
                    >
                      {t('FOOTER_NEWSLETTER_PRIVACY')}
                      <a
                        href={`https://www.iubenda.com/privacy-policy/${t(
                          'IUBENDA.POLICY_ID'
                        )}`}
                        className="iubenda-white no-brand iubenda-noiframe iub-body-embed text-primary"
                        title="Privacy Policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('PRIVACY_TITLE')}
                      </a>
                    </div>
                  </label>
                </div>
              </div>
              <div className="mt-6 max-w-lg">
                {loading ? (
                  <Spinner />
                ) : (
                  <button type="submit" className="w-full">
                    <SecondaryButtonBig text={t('SEND')} />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default withRecaptcha(B2BContacts);
